import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import Loader from "src/components/Dashboard/common/Loader";
import { getClinicUpdateRequest, reviewClinicUpdateRequest } from "src/components/Templates/api";


const ReviewClinicModal = ({ show, requestId, onClose, refetch }) => {
    const [requestData, setRequestData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const beforeSnapshot = React.useMemo(() => {
        try { return requestData?.beforeSnapshot ? JSON.parse(requestData.beforeSnapshot) : null; }
        catch { return null; }
    }, [requestData]);

    const afterSnapshot = React.useMemo(() => {
        try { return requestData?.afterSnapshot ? JSON.parse(requestData.afterSnapshot) : null; }
        catch { return null; }
    }, [requestData]);

    const getLogoUrl = (snapshot) => {
        if (snapshot?.public_logo?.domain && snapshot?.public_logo?.name) {
            return `${snapshot.public_logo.domain}/${snapshot.public_logo.name}`;
        }
        return null;
    };

    const getNewLogoUrl = (snapshot) => {
        if (snapshot?.public_logo?.domain && snapshot?.logoLink?.name && snapshot?.name) {
            return `${snapshot.public_logo.domain}/${snapshot.name}_logo.${snapshot.logoLink.name}`;
        }
        return null;
    };

    useEffect(() => {
        if (show && requestId) {
            fetchRequestDetails();
        }
    }, [show, requestId]);

    const fetchRequestDetails = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getClinicUpdateRequest(requestId);
            setRequestData(data);
        } catch (e) {
            setError(e.message || "Failed to load request details");
        }
        setLoading(false);
    };

    const handleSubmit = async (selectedAction) => {
        setSubmitting(true);

        const success = await reviewClinicUpdateRequest({
            requestId: requestId,
            action: selectedAction,
            comment: comment,
        });

        setSubmitting(false);
        if (success) {
            refetch(); // Refresh the data
            handleClose();
        }
    };

    const handleClose = () => {
        setRequestData(null);
        setLoading(false);
        setError(null);
        setComment("");
        setSubmitting(false);
        onClose();
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="lg"
            centered
            className="docg-modal"
            backdrop="static"
            keyboard={false}
        >
            <Modal.Header closeButton style={{ background: '#DEE2E6', color: '#6C5B7B' }}>
                <Modal.Title>Review Clinic Update Request</Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-4">
                <Loader show={loading} />

                {/* Error Message */}
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}

                {/* Content (shown when loaded successfully) */}
                {!loading && !error && requestData && (
                    <>
                        <div
                            style={{
                                borderBottom: "1px solid #ddd",
                                display: "flex",
                                justifyContent: "space-around",
                                padding: "12px 0"
                            }}
                        >
                            <p>
                                <strong>Clinic:</strong> {requestData.clinicName}
                            </p>
                            <p>
                                <strong>Requested By:</strong> {requestData.requestedBy}
                            </p>
                            <p>
                                <strong>Date:</strong>{" "}
                                {new Date(requestData.requestedAt).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                })}
                            </p>
                        </div>

                        {/* ===== Before/After Changes Table ===== */}
                        <div className="my-4">
                            <p
                                style={{
                                    textAlign: 'center',
                                    color: '#6C5B7B',
                                    fontWeight: '600',
                                    fontSize: '16px',

                                }}>
                                {requestData?.status === "Pending" ? "Changes for Review" : "Reviewed Changes"}
                            </p>

                            <table style={{ width: "95%", borderCollapse: "collapse", margin: "auto", fontSize: 'small' }}>
                                <thead>
                                    <th style={{ border: "1px solid #ccc", textAlign: "left", padding: "8px 12px", width: "25%" }}>Field</th>
                                    <th style={{ border: "1px solid #ccc", textAlign: "left", padding: "8px 12px", color: "#d32f2f" }}>Old Entry</th>
                                    <th style={{ border: "1px solid #ccc", textAlign: "left", padding: "8px 12px", color: "#388e3c" }}>New Entry</th>
                                </thead>
                                <tbody>
                                    {requestData.diff && requestData.diff.length > 0 ? (
                                        requestData.diff.map((item, idx) => (
                                            <tr key={idx}>
                                                <td style={{ border: "1px solid #ccc", textAlign: "left", padding: "8px 12px", fontWeight: '400' }}>
                                                    <strong>{item.field}</strong>
                                                </td>
                                                {/* <td style={{ border: "1px solid #ccc", textAlign: "left", padding: "8px 12px" }}>
                                                    {item.oldValue}
                                                </td>
                                                <td style={{ border: "1px solid #ccc", textAlign: "left", padding: "8px 12px" }}>
                                                    {item.newValue}
                                                </td> */}
                                                <td style={{ border: "1px solid #ccc", textAlign: "left", padding: "8px 12px" }}>
                                                    {item.field === "logoLink" ? (
                                                        getLogoUrl(beforeSnapshot) ? (
                                                            <img
                                                                src={getLogoUrl(beforeSnapshot)}
                                                                alt="Current Logo"
                                                                style={{ height: "60px", width: "60px", objectFit: "cover", borderRadius: "4px", border: "1px solid #ccc" }}
                                                            />
                                                        ) : (
                                                            <span style={{ color: "#999", fontSize: "12px" }}>No logo set</span>
                                                        )
                                                    ) : (
                                                        item.oldValue
                                                    )}
                                                </td>
                                                <td style={{ border: "1px solid #ccc", textAlign: "left", padding: "8px 12px" }}>
                                                    {item.field === "logoLink" ? (
                                                        getNewLogoUrl(afterSnapshot) ? (
                                                            <img
                                                                src={getNewLogoUrl(afterSnapshot)}
                                                                alt="New Logo"
                                                                style={{ height: "60px", width: "60px", objectFit: "cover", borderRadius: "4px", border: "1px solid #388e3c" }}
                                                                onError={(e) => {
                                                                    e.target.style.display = "none";
                                                                    e.target.nextSibling.style.display = "inline";
                                                                }}
                                                            />
                                                        ) : null
                                                    ) : (
                                                        item.newValue
                                                    )}
                                                    {item.field === "logoLink" && (
                                                        <span style={{ display: "none", color: "#999", fontSize: "12px" }}>
                                                            New logo (available after approval)
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="3" style={{ textAlign: "center" }}>
                                                No changes found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* ===== Review Form ===== */}
                        {requestData?.status === "Pending" && (
                            <div style={{ margin: "30px auto", width: "95%" }}>
                                <label style={{ marginBottom: "4px", display: "block", fontSize: "14px" }}>
                                    <strong>Comment (Optional)</strong>
                                </label>
                                <input
                                    className="form-control"
                                    type="text"
                                    placeholder="Add feedback or reason to reject.."
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    disabled={submitting}
                                    style={{ height: "50px", width: "100%", outline: "none", focus: "none" }}
                                />
                            </div>
                        )}
                    </>
                )}


                {/* ========== MODAL FOOTER ========== */}

                {requestData?.status === "Pending" ? (
                    <div className="d-flex justify-content-between align-items-center gap-2 pt-4" style={{ borderTop: "1px solid #ddd" }}>
                        <div className="d-flex justify-content-end my-2 gap-2">
                            <button
                                className="docg-outline-btn"
                                onClick={handleClose}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                            <button
                                className="docg-save-btn"
                                onClick={() => handleSubmit("Reject")}
                                disabled={submitting || loading}
                            >
                                Reject Request
                            </button>
                        </div>
                        <div>
                            <button
                                className="docg-save-btn"
                                onClick={() => handleSubmit("Approve")}
                                disabled={submitting || loading}
                                style={{ backgroundColor: "green" }}
                            >
                                Approve Request
                            </button>
                        </div>
                    </div>
                ) : (
                    <div style={{ borderTop: "1px solid #ddd" }}>
                        <button
                            className="docg-save-btn mt-4 mx-auto"
                            onClick={handleClose}
                            disabled={submitting}
                        >
                            Okay
                        </button>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default ReviewClinicModal;